import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Pagination, Table } from "rsuite";
import { SortType } from "rsuite-table";
import eyeIcon from "../../assets/img/eye-icon.svg";
import { AppRouteUi } from "../../config/appRoute";
import {
  ActionsBillingInvoiceTable,
  useAppDispatch,
  useAppSelector,
} from "../../core/redux";
import {
  ApiSchemaBillingInvoices,
  ApiSchemaPaginatedBillingInvoice,
} from "../../models/api";
import { sortTableData } from "../../utils/tableFilter";
import { PaginationEntriesInput } from "../paginationEntries";
import { DateCell } from "../tableItem.component";
import { convertToTwoDecimal } from "../../utils/convertToTwoDecimal";

const { Column, HeaderCell, Cell } = Table;

interface IProps {
  data: ApiSchemaPaginatedBillingInvoice | null | undefined;
}

export function BillingInvoiceList({ data }: IProps) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { pageNumber, pageSize, isLoading } = useAppSelector(
    (state) => state.billingInvoiceTable
  );

  const [sortType, setSortType] = useState<SortType>("desc");
  const [filteredData, setFilteredData] = useState<
    ApiSchemaPaginatedBillingInvoice["list"]
  >(data?.list || []);

  const handleSortColumn = (
    sortColumn: string,
    sortType: SortType | undefined
  ) => {
    setSortType(sortType!);
    const sortedData = sortTableData(
      filteredData,
      sortColumn as keyof ApiSchemaBillingInvoices,
      sortType === "asc"
    );
    setFilteredData(sortedData);
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    const sortedData = sortTableData(
      data.list,
      "billingDate",
      sortType === "asc"
    );
    setFilteredData(sortedData);
  }, [data, sortType]);

  return (
    <div>
      <div className="p-2 d-flex align-item-center">
        <div className="align-self-center me-2">Show</div>
        <PaginationEntriesInput
          onChange={(value) => {
            dispatch(ActionsBillingInvoiceTable.goToPage(1));
            dispatch(ActionsBillingInvoiceTable.setEntryCount(value as number));
          }}
        />
        <div className="align-self-center ms-2">entries</div>
      </div>

      <Table
        autoHeight
        data={filteredData}
        loading={isLoading}
        rowHeight={50}
        sortColumn={"billingDate"}
        sortType={sortType}
        onSortColumn={handleSortColumn}
      >
        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">Date</HeaderCell>
          <DateCell dataKey="billingDate" />
        </Column>
        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">Document ID</HeaderCell>
          <Cell dataKey="documentId" />
        </Column>
        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">Document Type</HeaderCell>
          <Cell>Invoice</Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell className="fw-bold text-black">Billed Amount</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaBillingInvoices) => (
              <span>
                ${" "}
                {convertToTwoDecimal(rowData.billedAmount as unknown as number)}
              </span>
            )}
          </Cell>
        </Column>
        <Column align="center" fixed="right" flexGrow={1}>
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <Cell>
            {(rowData: ApiSchemaBillingInvoices) => (
              <img
                src={eyeIcon}
                alt="View"
                className="me-2 click"
                title={"View Invoice"}
                height={20}
                onClick={() => {
                  history.push(
                    AppRouteUi.Billing.ViewInvoice(rowData.invoiceSKey),
                    rowData
                  );
                }}
              />
            )}
          </Cell>
        </Column>
      </Table>

      {data && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={filteredData.length}
            pages={
              data?.totalCount
                ? Math.ceil(data?.totalCount / pageSize)
                : Math.ceil(filteredData.length / pageSize)
            }
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page) =>
              dispatch(ActionsBillingInvoiceTable.goToPage(page))
            }
          />
        </div>
      )}
    </div>
  );
}
